import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Button, Alert, Typography, Checkbox, Modal, Divider, Space } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

interface EligibilityCheckStepProps {
    onNext: (values: EligibilityData) => void;
    dark: boolean;
    initialData?: Partial<EligibilityData>;
    minimumMonthlyContribution?: number;
    shareCapital?: number;
}

export interface EligibilityData {
    governmentEmployee: boolean;
    grossSalary: number;
    netSalary: number;
    monthlyDeductionAmount?: number;
    termsAccepted: boolean;
}

const EligibilityCheckStep: React.FC<EligibilityCheckStepProps> = ({ onNext, dark, initialData, minimumMonthlyContribution,shareCapital }) => {
    const [form] = Form.useForm();
    const [eligibilityStatus, setEligibilityStatus] = useState<{
        isEligible: boolean | null;
        meetsMinimum: boolean;
        meetsOneThirdRule: boolean;
    }>({ isEligible: null, meetsMinimum: false, meetsOneThirdRule: false });
    const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);

    const effectiveMinimumMonthlyContribution = minimumMonthlyContribution ?? 50000;
    const SHARE_CAPITAL_REQUIRED = shareCapital ?? 500000;

    // Initialize form with provided initial data if available
    useEffect(() => {
        if (initialData) {
            form.setFieldsValue({
                governmentEmployee: initialData.governmentEmployee,
                grossSalary: initialData.grossSalary,
                netSalary: initialData.netSalary,
                termsAccepted: initialData.termsAccepted || false
            });

            // Calculate eligibility status for initial data
            if (initialData.grossSalary && initialData.netSalary) {
                const status = calculateEligibility({
                    grossSalary: initialData.grossSalary,
                    netSalary: initialData.netSalary
                });
                setEligibilityStatus(status);
            }
        }
    }, [initialData, form]);

    const calculateEligibility = (values: any) => {
        const { grossSalary, netSalary } = values;

        if (!grossSalary || !netSalary) {
            return { isEligible: null, meetsMinimum: false, meetsOneThirdRule: false };
        }

        // Check if can contribute minimum monthly savings amount
        const meetsMinimum = netSalary >= effectiveMinimumMonthlyContribution;

        // Check one-third rule
        const remainingAfterDeduction = netSalary - effectiveMinimumMonthlyContribution;
        const meetsOneThirdRule = remainingAfterDeduction >= (grossSalary / 3);

        return {
            isEligible: meetsMinimum && meetsOneThirdRule,
            meetsMinimum,
            meetsOneThirdRule
        };
    };

    const handleValuesChange = () => {
        try {
            const values = form.getFieldsValue();
            if (values.grossSalary > 0 && values.netSalary > 0) {
                const status = calculateEligibility(values);
                setEligibilityStatus(status);
            } else {
                setEligibilityStatus({ isEligible: null, meetsMinimum: false, meetsOneThirdRule: false });
            }
        } catch (e) {
            console.error('Error calculating eligibility:', e);
        }
    };

    const showTerms = () => {
        setIsTermsModalVisible(true);
    };

    const handleSubmit = (values: any) => {
        if (!values.governmentEmployee) {
            Modal.error({
                title: 'Eligibility Error',
                content: 'Only government employees are eligible to register with Hazina SACCOS.',
            });
            return;
        }

        const status = calculateEligibility(values);

        if (!status.isEligible) {
            Modal.error({
                title: 'Eligibility Check Failed',
                content: 'You do not meet the minimum eligibility requirements (minimum monthly contribution and/or one-third rule). Registration cannot proceed.',
            });
            return;
        }

        onNext(values as EligibilityData);
    };

    return (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <Title level={4} className={dark ? 'text-white' : ''}>
                    Hazina SACCOS Eligibility Check
                </Title>
                <Text className={dark ? 'text-gray-300' : 'text-gray-600'}>
                    Please confirm your eligibility information for Hazina SACCOS membership
                </Text>
            </div>

            <Alert
                message="Hazina SACCOS Membership Requirements"
                description={
                    <div>
                        <p className="mb-3"><strong>To become a member of Hazina SACCOS, you must meet the following requirements:</strong></p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li><strong>Government Employment:</strong> Only government employees are eligible for membership</li>
                            <li><strong>Share Capital:</strong> Purchase shares worth <strong>TSH {new Intl.NumberFormat('en-TZ').format(SHARE_CAPITAL_REQUIRED)}</strong> upon joining</li>
                            <li><strong>Monthly Compulsory Savings:</strong> Automatic deduction of <strong>TSH {new Intl.NumberFormat('en-TZ').format(effectiveMinimumMonthlyContribution)}</strong> monthly through salary</li>
                            <li><strong>One-Third Rule:</strong> After all deductions, retain at least 1/3 of your gross salary</li>
                            <li><strong>Loan Eligibility:</strong> Access to various loan products based on "cooperative societies policies" compliance</li>
                            <li><strong>Documentation:</strong> Valid employment letter, pay slip, and government employee ID required</li>
                        </ul>
                    </div>
                }
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                className="mb-6"
            />

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                onValuesChange={handleValuesChange}
                requiredMark="optional"
            >
                <Form.Item
                    name="governmentEmployee"
                    valuePropName="checked"
                    rules={[{ required: true, transform: value => !!value, message: 'Please confirm your employment status' }]}
                >
                    <Checkbox className={dark ? 'text-white' : ''}>
                        I confirm that I am a government employee with valid employment credentials (employment letter, pay slip, and government employee ID)
                    </Checkbox>
                </Form.Item>

                <Form.Item
                    name="grossSalary"
                    label={<span className={dark ? 'text-white' : ''}>Gross Monthly Salary (TZS)</span>}
                    rules={[
                        { required: true, message: 'Please enter your gross monthly salary' },
                        { type: 'number', min: 1, message: 'Please enter a valid amount' }
                    ]}
                >
                    <InputNumber
                        style={{ width: '100%' }}
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                        placeholder="Enter your gross monthly salary"
                        className={dark ? 'bg-gray-700 text-white' : ''}
                    />
                </Form.Item>

                <Form.Item
                    name="netSalary"
                    label={<span className={dark ? 'text-white' : ''}>Net Monthly Salary (after existing deductions) (TZS)</span>}
                    rules={[
                        { required: true, message: 'Please enter your net monthly salary' },
                        { type: 'number', min: 1, message: 'Please enter a valid amount' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('grossSalary') >= value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('Net salary cannot be greater than gross salary'));
                            },
                        }),
                    ]}
                >
                    <InputNumber
                        style={{ width: '100%' }}
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                        placeholder="Enter your net monthly salary"
                        className={dark ? 'bg-gray-700 text-white' : ''}
                    />
                </Form.Item>

                <Form.Item
                    name="monthlyDeductionAmount"
                    label={<span className={dark ? 'text-white' : ''}>Desired Monthly Deduction Amount (TZS)</span>}
                    rules={[
                        { required: true, message: 'Please enter your desired monthly deduction amount' },
                        { type: 'number', min: effectiveMinimumMonthlyContribution, message: `Minimum deduction amount is TZS ${new Intl.NumberFormat('en-TZ').format(effectiveMinimumMonthlyContribution)}` },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                const netSalary = getFieldValue('netSalary');
                                const grossSalary = getFieldValue('grossSalary');
                                if (!value || !netSalary || !grossSalary) {
                                    return Promise.resolve();
                                }
                                const remainingAfterDeduction = netSalary - value;
                                const oneThirdOfGross = grossSalary / 3;
                                if (remainingAfterDeduction >= oneThirdOfGross) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('Deduction amount violates the one-third rule. You must retain at least 1/3 of your gross salary after deductions.'));
                            },
                        }),
                    ]}
                    extra={<Text type="secondary" style={{ fontSize: '12px' }}>This amount will be automatically deducted from your salary each month. Minimum: TZS {new Intl.NumberFormat('en-TZ').format(effectiveMinimumMonthlyContribution)}</Text>}
                >
                    <InputNumber
                        style={{ width: '100%' }}
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                        placeholder="Enter your desired monthly deduction amount"
                        className={dark ? 'bg-gray-700 text-white' : ''}
                    />
                </Form.Item>

                {eligibilityStatus.isEligible !== null && (
                    <Alert
                        message={eligibilityStatus.isEligible ? "✓ You meet the eligibility requirements" : "⚠️ You may not meet the eligibility requirements"}
                        description={
                            <div>
                                <p><strong>Financial Requirements Assessment:</strong></p>
                                <div className="grid grid-cols-1 gap-3 mt-3">
                                    <div className="p-3 border rounded">
                                        <p><strong>Share Capital Required:</strong></p>
                                        <p>TSH {new Intl.NumberFormat('en-TZ').format(SHARE_CAPITAL_REQUIRED)} (one-time payment)</p>
                                    </div>
                                    <div className="p-3 border rounded">
                                        <p><strong>Monthly Compulsory Savings:</strong></p>
                                        <p>TSH {new Intl.NumberFormat('en-TZ').format(effectiveMinimumMonthlyContribution)}
                                            <Text type={eligibilityStatus.meetsMinimum ? "success" : "danger"}>
                                                {" - " + (eligibilityStatus.meetsMinimum ? "✓ Can afford" : "✗ Cannot afford")}
                                            </Text></p>
                                    </div>
                                    <div className="p-3 border rounded">
                                        <p><strong>One-Third Rule:</strong></p>
                                        <p>Must retain at least TSH {new Intl.NumberFormat('en-TZ').format(Math.round((form.getFieldValue('grossSalary') || 0) / 3))} after all deductions</p>
                                        <Text type={eligibilityStatus.meetsOneThirdRule ? "success" : "danger"}>
                                            {eligibilityStatus.meetsOneThirdRule ? "✓ Requirement met" : "✗ Requirement not met"}
                                        </Text>
                                    </div>
                                </div>
                                {!eligibilityStatus.isEligible && (
                                    <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded">
                                        <p className="text-orange-800 font-medium">
                                            <strong>⚠️ Important Notice:</strong> If you proceed, your application will be subject to review by Hazina SACCOS.
                                            There may be processing fees and charges that are not fully refundable if your application is not approved.
                                        </p>
                                    </div>
                                )}
                            </div>
                        }
                        type={eligibilityStatus.isEligible ? "success" : "warning"}
                        showIcon
                        className="mb-6"
                    />
                )}

                <Form.Item
                    name="termsAccepted"
                    valuePropName="checked"
                    rules={[
                        {
                            validator: (_, value) =>
                                value ? Promise.resolve() : Promise.reject(new Error('You must accept the terms and conditions'))
                        }
                    ]}
                >
                    <Checkbox className={dark ? 'text-white' : ''}>
                        I have read and agree to the <Button type="link" onClick={showTerms} className="p-0">Hazina SACCOS Terms and Conditions</Button>
                    </Checkbox>
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit" size="large" block>
                        Confirm Eligibility & Continue
                    </Button>
                </Form.Item>
            </Form>

            <Modal
                title="Hazina SACCOS Terms and Conditions"
                open={isTermsModalVisible}
                onOk={() => setIsTermsModalVisible(false)}
                onCancel={() => setIsTermsModalVisible(false)}
                width={800}
                footer={[
                    <Button key="close" type="primary" onClick={() => setIsTermsModalVisible(false)}>
                        I Understand
                    </Button>
                ]}
            >
                <div className="space-y-4 max-h-96 overflow-y-auto p-2">
                    <Title level={4}>HAZINA SACCOS MEMBERSHIP TERMS AND CONDITIONS</Title>

                    <Title level={5}>1. MEMBERSHIP ELIGIBILITY</Title>
                    <Paragraph>
                        <ul className="list-disc pl-5 space-y-1">
                            <li><strong>Government Employment Requirement:</strong> Membership is exclusively available to government employees. Applicants must provide valid proof of government employment including official employment letter, current pay slip, and government employee identification.</li>
                            <li><strong>Verification Process:</strong> All employment credentials will be verified with the respective government departments before membership approval.</li>
                        </ul>
                    </Paragraph>

                    <Title level={5}>2. FINANCIAL REQUIREMENTS</Title>
                    <Paragraph>
                        <ul className="list-disc pl-5 space-y-1">
                            <li><strong>Share Capital:</strong> Every member is required to purchase shares worth <strong>TSH 500,000</strong> (Five Hundred Thousand Tanzanian Shillings) upon joining. This represents the member's equity stake in the cooperative.</li>
                            <li><strong>Monthly Compulsory Savings:</strong> Members must authorize automatic monthly deductions of <strong>TSH {new Intl.NumberFormat('en-TZ').format(effectiveMinimumMonthlyContribution)}</strong> for compulsory savings through salary deduction.</li>
                            <li><strong>One-Third Rule:</strong> After all deductions, including SACCOS contributions, members must remain with at least one-third (1/3) of their gross monthly salary.</li>
                            <li><strong>Payment Terms:</strong> Share capital must be paid in full before membership activation. Monthly savings deductions begin in the first month following membership approval.</li>
                        </ul>
                    </Paragraph>

                    <Title level={5}>3. LOAN PRODUCTS AND ELIGIBILITY</Title>
                    <Paragraph>
                        <ul className="list-disc pl-5 space-y-1">
                            <li><strong>Loan Access:</strong> Members become eligible for various loan products based on their compliance with "Sera za Ushirika" (Cooperative Bylaws), including personal development loans, emergency loans, asset financing loans, and business development loans.</li>
                            <li><strong>Qualification Criteria:</strong> Loan eligibility is determined by length of membership, savings history and consistency, share capital contribution, adherence to cooperative bylaws, and credit history within the cooperative.</li>
                        </ul>
                    </Paragraph>

                    <Title level={5}>4. MEMBER OBLIGATIONS</Title>
                    <Paragraph>
                        <strong>Members must:</strong>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Maintain continuous government employment</li>
                            <li>Honor automatic deduction agreements</li>
                            <li>Comply with all cooperative bylaws and regulations ("Sera za Ushirika")</li>
                            <li>Attend required meetings and training sessions</li>
                            <li>Provide accurate and updated personal information</li>
                            <li>Immediately notify the SACCOS of changes in employment status or contact information</li>
                        </ul>
                    </Paragraph>

                    <Title level={5}>5. TERMINATION AND WITHDRAWAL</Title>
                    <Paragraph>
                        <ul className="list-disc pl-5 space-y-1">
                            <li><strong>Voluntary Withdrawal:</strong> Members may withdraw by providing 30 days written notice, subject to settlement of all outstanding obligations.</li>
                            <li><strong>Involuntary Termination:</strong> Membership may be terminated for cessation of government employment, failure to meet financial obligations, violation of cooperative bylaws, or fraudulent activities.</li>
                        </ul>
                    </Paragraph>

                    <Title level={5}>6. FEES AND CHARGES</Title>
                    <Paragraph>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Registration fees and initial deposits may be subject to processing charges</li>
                            <li>If an application is rejected or withdrawn after submission, certain charges may apply and may not be fully refundable</li>
                            <li>All applicable fees will be clearly communicated during the registration process</li>
                        </ul>
                    </Paragraph>

                    <Title level={5}>7. DATA PRIVACY AND SECURITY</Title>
                    <Paragraph>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>By submitting this application, you consent to Hazina SACCOS collecting, processing, and storing your personal and financial information</li>
                            <li>Your information will be used for evaluating membership eligibility and providing SACCOS services</li>
                            <li>Hazina SACCOS will protect your data in accordance with applicable data protection laws</li>
                            <li>Members are responsible for maintaining the confidentiality of their portal login credentials</li>
                        </ul>
                    </Paragraph>

                    <Title level={5}>8. DISPUTE RESOLUTION</Title>
                    <Paragraph>
                        <ul className="list-disc pl-5 space-y-1">
                            <li><strong>Internal Resolution:</strong> Disputes shall first be addressed through internal cooperative mechanisms as outlined in the "Sera za Ushirika"</li>
                            <li><strong>Legal Jurisdiction:</strong> These terms are governed by the laws of the United Republic of Tanzania</li>
                        </ul>
                    </Paragraph>

                    <Divider />

                    <Paragraph strong className="text-center bg-gray-50 p-3 rounded">
                        By accepting these terms and conditions, you acknowledge that you have read, understood, and agree to be bound by all the provisions outlined above. The SACCOS reserves the right to amend these terms with 30 days notice to all members.
                    </Paragraph>
                </div>
            </Modal>
        </div>
    );
};

export default EligibilityCheckStep;